import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardDescription, CardFooter,
  CardHeader, CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuthSession } from '@/hooks/useAuthSession';
import { supabase } from '@/lib/supabase';
import { storePaymentBackup } from '@/utils/PaymentHelper';
import { getStoredReferralCode, attributeReferralToSubscription } from '@/utils/AffiliateUtils';
import { CheckCircle2 } from 'lucide-react';

const PLAN_MAP: Record<string, 'trial' | 'basic' | 'pro' | 'free'> = {
  'trial': 'trial',
  'basic': 'basic',
  'pro': 'pro',
  'free': 'free',
  '67b91852-05c3-4bd6-9e74-409d258dfe8d': 'trial',
  'd17e8438-ad89-4ac0-a722-7496c65de0a8': 'basic',
  '2f9f9004-dbaf-42d3-979b-289e7094d535': 'pro',
  '0ff85d8e-8dca-4a2d-b265-a2d0549b6818': 'free'
};

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { session } = useAuthSession();
  const navigate = useNavigate();

  const userId = session?.user?.id;
  const planIdParam = searchParams.get('plan_id') ?? '';
  const readablePlanId = PLAN_MAP[planIdParam.toLowerCase()] || planIdParam;
  const billingCycleParam = searchParams.get('billing_cycle');
  const subscriptionId = searchParams.get('subscription_id') || null;
  const paymentMethod = searchParams.get('payment_method') || 'razorpay';
  const paymentId = searchParams.get('payment_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (!userId) return;

    const verifyAndStore = async () => {
      try {
        if (!planIdParam || !billingCycleParam)
          throw new Error("Missing plan ID or billing cycle");

        const isTrial = readablePlanId === 'trial';
        const isFree = readablePlanId === 'free';

        if (isTrial) {
          const { data: priorTrial } = await supabase
            .from('user_subscriptions')
            .select('id')
            .eq('user_id', userId)
            .eq('is_trial', true)
            .not('plan_id', 'eq', 'trial')
            .limit(1)
            .maybeSingle();

          if (priorTrial) throw new Error("You have already used the trial plan.");
        }

        const currentStart = new Date();
        let currentEnd = new Date(currentStart);
        let billingCycle = billingCycleParam;

        if (isTrial || isFree) {
          currentEnd.setDate(currentEnd.getDate() + 7);
          billingCycle = 'one-time';
        } else if (billingCycleParam === 'monthly') {
          currentEnd.setMonth(currentEnd.getMonth() + 1);
        } else if (billingCycleParam === 'yearly') {
          currentEnd.setFullYear(currentEnd.getFullYear() + 1);
        }

        const subscriptionData: any = {
          plan_id: planIdParam,
          billing_cycle: billingCycle,
          status: 'active',
          payment_method: paymentMethod,
          is_trial: isTrial,
          current_period_start: currentStart.toISOString(),
          current_period_end: currentEnd.toISOString(),
          updated_at: new Date().toISOString(),
          features: getPlanFeatures(readablePlanId)
        };

        if (isTrial) {
          subscriptionData.trial_ends_at = currentEnd.toISOString();
        }

        storePaymentBackup({
          planId: readablePlanId,
          billingCycle,
          paymentId,
          orderId,
          timestamp: new Date().toISOString(),
          notes: { referrer: document.referrer }
        });

        const referralCode = getStoredReferralCode();
        if (referralCode && subscriptionId) {
          await attributeReferralToSubscription(subscriptionId, userId);
        }

        const { error: upsertError } = await supabase
          .from('user_subscriptions')
          .upsert({ user_id: userId, ...subscriptionData }, { onConflict: 'user_id' });

        if (upsertError) throw upsertError;

        toast.success("Subscription activated!");
        setSuccess(true);
      } catch (err: any) {
        console.error('Subscription verification error:', err);
        setError(err?.message || "Failed to activate subscription. Please contact support.");
      } finally {
        setLoading(false);
      }
    };

    verifyAndStore();
  }, [userId, planIdParam, billingCycleParam, subscriptionId, paymentMethod, paymentId, orderId]);

  return (
    <div className="container max-w-xl py-10">
      <Card className="shadow-xl border-green-100">
        <CardHeader>
          <CardTitle className="text-2xl font-bold flex items-center gap-2 text-green-600">
            <CheckCircle2 className="h-6 w-6" />
            Subscription Success!
          </CardTitle>
          <CardDescription className="text-base text-gray-700">
            {success && "Your Edaptica journey just got supercharged!"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-b-transparent"></div>
            </div>
          ) : error ? (
            <div className="text-red-700 bg-red-100 p-4 rounded-md border border-red-300 text-sm">
              {error}
            </div>
          ) : (
            <div className="text-green-800 bg-green-50 border border-green-200 rounded-md p-4 text-center space-y-2">
              <h3 className="text-lg font-semibold">Welcome to the Future of Learning 🚀</h3>
              <p>You've unlocked premium features that will transform the way you study.</p>
              <p className="text-sm text-gray-600 italic">
                “Success is the sum of small efforts, repeated day in and day out.” – Robert Collier
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button asChild className="w-full">
            <Link to="/dashboard">Go to Dashboard</Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

function getPlanFeatures(planId: string): string[] {
  const free = [
    "Adaptive Quiz Assessment", "Quiz History", "Adaptive Study Notes",
    "Adaptive Current Affairs", "Adaptive Flash Cards"
  ];
  const basic = [...free, "Advanced Analytics"];
  const pro = [
    ...basic,
    "Adaptive Learning Path", "Adaptive Video Recommendation",
    "Adaptive Mock Tests", "PYQ Multiplier", "AI Learning Studio",
    "Collaborative Learning", "Code Playground", "Skill-gap Analysis"
  ];
  if (planId === 'free') return free;
  if (planId === 'basic') return basic;
  if (planId === 'pro' || planId === 'trial') return pro;
  return [];
}

export default SubscriptionSuccess;
